import cn from "classnames"
import { DynamicHeadline } from "components/DynamicHeadline"
import pauseSvg from "/public/pause.svg"
import playSvg from "/public/play.svg"

import { RenderBardWithComponents } from "components/Wysiwyg"
import { useRef, useState } from "react"
import Section, { parseColors } from "components/Section"
import Letter from "components/ui/Letter"
import LinkButton from "components/ui/LinkButton"
import styles from "../TextMedia.module.scss"

const TextVideo = ({ data = {}, colors, content }) => {
  const {
    textVideoText: text,
    video,
    video_orientation,
    letter: letterObj,
    link: url,
    link_text: linkText,
    new_tab: newTab,
    headline_teaser: headlineTeaser,
    dynamic_headline: headline,
    headline_type: headlineType,
    headline_appearance: headlineAppearance,
    text_video_fill_background: fillBg,
    cta_link: cta_Link,
    ankerslug,
  } = data

  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const playVideo = () => {
    if (!videoRef?.current) return

    if (!isPlaying) {
      setIsPlaying(true)
      videoRef.current.play()
    } else {
      setIsPlaying(false)
      videoRef.current.pause()
    }
  }

  if (!video?.is_video) return null

  const { accent, background } = parseColors({ colors, fillBg })

  const letter = letterObj?.value || "v"
  const hasVideo = !!video
  const hasLetter = letterObj && letter !== "none"
  const hasBg = fillBg && background.color !== "transparent"
  const graphicRight = video_orientation?.value === "right"

  return (
    <Section
      sectionSpacing={{
        top: data?.section_padding_top?.value || null,
        bottom: data?.section_padding_bottom?.value || null,
      }}
      id={ankerslug}
      className={cn(styles.textMediaWrapper, {
        [styles.filled]: hasBg,
        [styles.letterOnly]: hasLetter && !hasVideo,
        [styles.mediaOnly]: !hasLetter && hasVideo,
        [styles[`textMediaWrapper--${content.locale}`]]: content.locale,
      })}
      data={data}
      colors={colors}
    >
      {headline && headlineTeaser && (
        <p className={styles.headlineTeaser}>{headlineTeaser}</p>
      )}
      <DynamicHeadline
        headlineType={headlineType.value}
        headlineAppearance={headlineAppearance.value}
      >
        {headline}
      </DynamicHeadline>
      <div
        className={cn(styles.textMedia, {
          [styles.flexReverse]: hasVideo && graphicRight,
        })}
      >
        <div className={styles.graphicWrapper}>
          {hasLetter && (
            <Letter
              type={letter}
              fill={accent.color}
              className={cn(styles.letter, {
                [styles.letterLeft]: !graphicRight,
                [styles.letterRight]: graphicRight,
                [styles.letterReverse]: hasLetter && graphicRight,
              })}
            />
          )}
          {hasVideo && (
            <div className={cn(styles.mediaWrapper, "group")}>
              <video
                ref={videoRef}
                src={video.permalink}
                onEnded={() => setIsPlaying(false)}
              />
              <div
                className={cn(styles.playButtonWrapper, {
                  "hidden group-hover:flex": isPlaying,
                  "flex ": !isPlaying,
                })}
              >
                <button onClick={playVideo}>
                  {!isPlaying ? (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        className="translate-x-0.5"
                        alt="Play icon"
                        src={playSvg.src}
                      />
                    </>
                  ) : (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img alt="Pause icon" src={pauseSvg.src} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.contentWrapper}>
          {!headline && headlineTeaser && (
            <p className={styles.headlineTeaser}>{headlineTeaser}</p>
          )}
          <div className={styles.text}>
            <RenderBardWithComponents content={text} prefix="text_" />
          </div>
          {url && (
            <LinkButton
              className={cn(
                styles.button,
                cta_Link ? `button--cta ${styles.ctaLink}` : `button`
              )}
              data={{
                link_url: url,
                link_text: linkText,
                link_blank: newTab,
              }}
            />
          )}
        </div>
      </div>
    </Section>
  )
}

export default TextVideo
